import { useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { wsUrl } from "@/lib/api";

/**
 * Opens one persistent WebSocket to /api/ws/dms per authed session. Every DM
 * (sent OR received by this user) is fanned out here so DMInbox + DMThread
 * update instantly instead of waiting on the 4s REST poll.
 *
 * Emits a `nw:new-dm` CustomEvent on `window` with detail: { message }.
 * Reconnects with capped exponential backoff. Keepalive ping every 25s to
 * survive Kubernetes ingress idle timeouts (~60s).
 */
export function useDMSocket() {
  const { user, token } = useAuth();
  const wsRef = useRef(null);
  const pingRef = useRef(null);
  const reconnectRef = useRef(null);
  const attemptsRef = useRef(0);
  const shouldRunRef = useRef(true);

  useEffect(() => {
    if (!user || user === false || !token) return;
    shouldRunRef.current = true;

    const cleanupTimers = () => {
      if (pingRef.current) { clearInterval(pingRef.current); pingRef.current = null; }
      if (reconnectRef.current) { clearTimeout(reconnectRef.current); reconnectRef.current = null; }
    };

    const connect = () => {
      if (!shouldRunRef.current) return;
      const url = `${wsUrl("/ws/dms")}?token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        attemptsRef.current = 0;
        pingRef.current = setInterval(() => {
          if (ws.readyState === 1) {
            try { ws.send(JSON.stringify({ type: "ping" })); } catch { /* noop */ }
          }
        }, 25000);
      };

      ws.onmessage = (ev) => {
        let data;
        try { data = JSON.parse(ev.data); } catch { return; }
        if (data.type === "dm" && data.message) {
          window.dispatchEvent(new CustomEvent("nw:new-dm", { detail: { message: data.message } }));
        }
      };

      ws.onclose = (ev) => {
        cleanupTimers();
        if (!shouldRunRef.current) return;
        // Bad token — stop trying
        if (ev && ev.code === 4401) return;
        const delay = Math.min(30000, 1000 * Math.pow(2, attemptsRef.current++));
        reconnectRef.current = setTimeout(connect, delay);
      };

      ws.onerror = () => { /* handled by onclose */ };
    };

    connect();

    return () => {
      shouldRunRef.current = false;
      cleanupTimers();
      try { wsRef.current?.close(1000, "unmount"); } catch { /* noop */ }
      wsRef.current = null;
    };
  }, [user, token]);
}
